import { CutRuleEngine } from './CutRuleEngine';
import type { LeaderboardEntry, SeasonEvent, TournamentState } from './types';
export class TournamentController {
  readonly cutEngine = new CutRuleEngine();
  create(
    event: SeasonEvent,
    field: { id: string; name: string; shortName?: string }[],
    playerId: string,
    seed = 1,
  ): TournamentState {
    const leaderboard: LeaderboardEntry[] = field.map((g, i) => ({
      golferId: g.id,
      golferName: g.name,
      golferShortName: g.shortName,
      position: i + 1,
      currentRound: 1,
      currentHole: 0,
      today: 0,
      total: 0,
      movement: 0,
      cutStatus: 'safe',
      finished: false,
      isPlayer: g.id === playerId,
    }));
    const ids = field.map((g) => g.id);
    return {
      eventId: event.id,
      courseId: event.courseId,
      currentRound: 1,
      currentHole: 1,
      playerField: ids,
      leaderboard,
      roundScores: {},
      holeScores: {},
      cumulativeScores: Object.fromEntries(ids.map((id) => [id, 0])),
      cutStatus: {},
      projectedCut: null,
      pairings: this.pair(ids),
      playerPairing: [],
      tournamentStatus: 'roundInProgress',
      completedRounds: [],
      finalResult: null,
      simulationSeed: seed,
    };
  }
  recordHole(state: TournamentState, golferId: string, score: number, par: number) {
    const rounds = (state.holeScores[golferId] ??= []);
    const holes = rounds[state.currentRound - 1] ?? [];
    holes.push(score);
    rounds[state.currentRound - 1] = holes;
    state.holeScores[golferId] = rounds;
    state.cumulativeScores[golferId] = (state.cumulativeScores[golferId] ?? 0) + (score - par);
    const e = state.leaderboard.find((x) => x.golferId === golferId);
    if (e) {
      e.currentHole = holes.length;
      e.today = holes.reduce(
        (s, v, i) =>
          s + v - (state.holeScores[golferId][state.currentRound - 1][i] === undefined ? 0 : par),
        0,
      );
      e.total = state.cumulativeScores[golferId];
    }
    this.rank(state);
  }
  completeRound(state: TournamentState, event: SeasonEvent, playerId: string) {
    if (!state.completedRounds.includes(state.currentRound))
      state.completedRounds.push(state.currentRound);
    for (const e of state.leaderboard) {
      const holes = state.holeScores[e.golferId]?.[state.currentRound - 1] ?? [];
      (state.roundScores[e.golferId] ??= []).push(holes.reduce((s, v) => s + v, 0));
      e.finished = true;
    }
    if (state.currentRound === 2 && event.cutRule.enabled) {
      state.tournamentStatus = 'cutProcessing';
      const result = this.cutEngine.evaluate(state.leaderboard, event.cutRule.topAndTies, true);
      state.projectedCut = result.cutLineScore;
      state.cutStatus = result.playerStatus;
      state.leaderboard.forEach((e) => (e.cutStatus = result.playerStatus[e.golferId]));
      if (result.playersMissingCut.includes(playerId)) {
        state.tournamentStatus = 'playerMissedCut';
        return state;
      }
    }
    if (state.currentRound >= event.rounds) {
      state.tournamentStatus = 'completed';
      this.rank(state);
      state.finalResult = state.leaderboard.find((e) => e.golferId === playerId) ?? null;
      return state;
    }
    state.currentRound++;
    state.currentHole = 1;
    state.leaderboard
      .filter((e) => e.cutStatus !== 'missedCut')
      .forEach((e) => {
        e.currentRound = state.currentRound;
        e.currentHole = 0;
        e.today = 0;
        e.finished = false;
      });
    state.pairings = this.pair(
      state.leaderboard
        .filter((e) => e.cutStatus !== 'missedCut')
        .sort((a, b) => a.position - b.position)
        .map((e) => e.golferId),
    );
    state.playerPairing = state.pairings.find((p) => p.includes(playerId)) ?? [];
    state.tournamentStatus = state.currentRound === 4 ? 'finalRound' : 'betweenRounds';
    return state;
  }
  updateProjectedCut(state: TournamentState, event: SeasonEvent) {
    if (state.currentRound !== 2 || !event.cutRule.enabled) return null;
    const r = this.cutEngine.evaluate(state.leaderboard, event.cutRule.topAndTies, false);
    state.projectedCut = r.projectedCutLineScore;
    state.leaderboard.forEach((e) => (e.cutStatus = r.playerStatus[e.golferId]));
    return r;
  }
  private rank(s: TournamentState) {
    const previousPositions = new Map(
      s.leaderboard.map((entry) => [entry.golferId, entry.position]),
    );
    s.leaderboard.sort((a, b) => a.total - b.total);
    let previous = NaN,
      position = 0;
    s.leaderboard.forEach((e, i) => {
      if (e.total !== previous) position = i + 1;
      e.position = position;
      e.movement = (previousPositions.get(e.golferId) ?? position) - position;
      previous = e.total;
    });
  }
  private pair(ids: string[]) {
    const out: string[][] = [];
    for (let i = 0; i < ids.length; i += 2) out.push(ids.slice(i, i + 2));
    return out;
  }
}
